import {fetchTopRepositories, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {CodespacesIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, ButtonGroup, IconButton} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {useCopilotAgent} from '../../../hooks/use-copilot-agent'
import styles from './CopilotAgentModeButton.module.css'
import {CopilotRepositoryPicker} from './CopilotRepositoryPicker'

export type CopilotAgentModeButtonProps = {
  repo: {id: string; name: string; owner: {login: string}}
  issueId: number
  onError: (error: string) => void
}

export const CopilotAgentModeButton = ({repo, issueId, onError}: CopilotAgentModeButtonProps) => {
  const environment = useRelayEnvironment()
  const [topRepos, setTopRepos] = useState<RepositoryPickerRepository[] | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRepo, setSelectedRepo] = useState(repo)

  const anchorRef = useRef(null)

  const {codespaceCreationError, handleCopilotAgentModeSubmit} = useCopilotAgent({
    owner: selectedRepo.owner.login,
    name: selectedRepo.name,
    issueDatabaseId: issueId || 0,
  })

  useEffect(() => {
    if (codespaceCreationError) {
      onError(codespaceCreationError)
    }
  }, [codespaceCreationError, onError])

  useEffect(() => {
    if (selectedRepo.id !== repo.id) {
      handleCopilotAgentModeSubmit()
    }
  }, [handleCopilotAgentModeSubmit, repo.id, selectedRepo.id])

  const onRepoSelected = useCallback(
    (nextRepo: {id: string; name: string; owner: {login: string}}) => {
      setSelectedRepo(prev => {
        if (prev.id === nextRepo.id) {
          handleCopilotAgentModeSubmit()
        }
        return nextRepo
      })
      setIsOpen(false)
    },
    [setSelectedRepo, setIsOpen, handleCopilotAgentModeSubmit],
  )

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      onError('')
      if (selectedRepo.id) {
        handleCopilotAgentModeSubmit()
      }
    },
    [selectedRepo.id, handleCopilotAgentModeSubmit, onError],
  )

  const handleIconClick = useCallback(async () => {
    onError('')
    if (!isLoading && !isOpen && !topRepos) {
      setIsLoading(true)

      const data = await fetchTopRepositories(environment)
      if (data) {
        setTopRepos(data)
        setIsLoading(false)
        setIsOpen(true)
      }
    } else {
      setIsOpen(!isOpen)
    }
  }, [environment, isLoading, isOpen, onError, topRepos])

  return (
    <>
      <ButtonGroup className={styles.buttonGroup}>
        <Button
          size="small"
          leadingVisual={CodespacesIcon}
          onClick={handleButtonClick}
          data-testid="open-in-copilot-agent-button"
          block
        >
          {LABELS.development.copilot.openAgent}
        </Button>
        <IconButton
          size="small"
          loading={isLoading}
          ref={anchorRef}
          icon={TriangleDownIcon}
          aria-label="Select code repository"
          onClick={handleIconClick}
          aria-disabled={isLoading}
        />
      </ButtonGroup>
      <CopilotRepositoryPicker
        topRepos={topRepos || []}
        anchorRef={anchorRef}
        isOpen={isOpen}
        onSelect={onRepoSelected}
        onClose={() => isOpen && setIsOpen(false)}
      />
    </>
  )
}
